import { useState, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import {
  adminSearchUsers,
  adminSetUserRole,
  adminGetLeads,
  adminGetReviewProjects,
  adminReviewProject,
  adminGetReviewProjectById,
  adminDeleteReviewProject,
  adminGetCompanyProfileImports,
  adminGetCompanyProfileImportById,
  adminReviewCompanyProfileImport,
} from '../utils/api';
import ProjectViewer from './ProjectViewer';
import type {
  AdminUserResult,
  AdminReviewProject,
  AdminCompanyProfileImport,
  AdminCompanyProfileDetailResponse,
  Project,
  Superteam,
  UserRole,
  CompanyProfileReviewStatus,
} from '../types';

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
  superteams: Superteam[];
}

export default function AdminPanel({ isOpen, onClose, superteams }: AdminPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AdminUserResult[]>([]);
  const [leads, setLeads] = useState<(AdminUserResult & { superteamName?: string })[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedSuperteam, setSelectedSuperteam] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [reviewProjects, setReviewProjects] = useState<AdminReviewProject[]>([]);
  const [reviewFilter, setReviewFilter] = useState<'pending_review' | 'needs_more_data' | 'approved' | 'all'>('pending_review');
  const [reviewProjectDetail, setReviewProjectDetail] = useState<Project | null>(null);
  const [reviewLoadingProjectId, setReviewLoadingProjectId] = useState<string | null>(null);
  const [companyImports, setCompanyImports] = useState<AdminCompanyProfileImport[]>([]);
  const [companyFilter, setCompanyFilter] = useState<CompanyProfileReviewStatus | 'all'>('pending_review');
  const [companyDetail, setCompanyDetail] = useState<AdminCompanyProfileDetailResponse['company'] | null>(null);
  const [companyLoadingId, setCompanyLoadingId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadCompanyImports = async (status: CompanyProfileReviewStatus | 'all' = companyFilter) => {
    try {
      const response = await adminGetCompanyProfileImports(status);
      if (response.success && response.data?.companies) {
        setCompanyImports(response.data.companies);
      }
    } catch (err) {
      console.error('Failed to load company profile imports:', err);
    }
  };

  // Load leads when panel opens
  useEffect(() => {
    if (isOpen) {
      loadLeads();
      loadReviewProjects('pending_review');
      loadCompanyImports('pending_review');
    }
  }, [isOpen]);

  const loadLeads = async () => {
    try {
      const response = await adminGetLeads();
      if (response.success && response.data?.leads) {
        setLeads(response.data.leads);
      }
    } catch (err) {
      console.error('Failed to load leads:', err);
    }
  };

  const loadReviewProjects = async (
    status: 'pending_review' | 'needs_more_data' | 'approved' | 'all' = reviewFilter
  ) => {
    try {
      const response = await adminGetReviewProjects(status);
      if (response.success && response.data?.projects) {
        setReviewProjects(response.data.projects);
      }
    } catch (err) {
      console.error('Failed to load review projects:', err);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminSearchUsers(searchQuery);
        if (response.success && response.data?.users) {
          setSearchResults(response.data.users);
        }
      } catch (err: any) {
        setError(err.message || 'Search failed');
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSetRole = useCallback(async (userId: string, role: UserRole) => {
    const superteamId = selectedSuperteam[userId];
    
    if (role === 'lead' && !superteamId) {
      setError('Please select a superteam first');
      return;
    }

    setActionLoading(userId);
    setError(null);
    
    try {
      const response = await adminSetUserRole(userId, role, superteamId);
      if (response.success) {
        // Refresh search results and leads
        if (searchQuery.length >= 2) {
          const searchResponse = await adminSearchUsers(searchQuery);
          if (searchResponse.success && searchResponse.data?.users) {
            setSearchResults(searchResponse.data.users);
          }
        }
        loadLeads();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  }, [selectedSuperteam, searchQuery]);

  const handleReviewAction = useCallback(async (projectId: string, action: 'approve' | 'needs_more_data') => {
    setActionLoading(`project-${projectId}`);
    setError(null);
    try {
      const response = await adminReviewProject(projectId, action);
      if (response.success) {
        await loadReviewProjects(reviewFilter);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update project review status');
    } finally {
      setActionLoading(null);
    }
  }, [reviewFilter]);

  const handleOpenReviewModal = useCallback(async (projectId: string) => {
    setReviewLoadingProjectId(projectId);
    setError(null);
    try {
      const response = await adminGetReviewProjectById(projectId);
      if (response.success && response.data?.project) {
        setReviewProjectDetail(response.data.project);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load project details');
    } finally {
      setReviewLoadingProjectId(null);
    }
  }, []);

  const handleCompanyReviewAction = useCallback(
    async (companyId: string, action: 'approve' | 'reject') => {
      setActionLoading(`company-${companyId}`);
      setError(null);
      try {
        const response = await adminReviewCompanyProfileImport(companyId, action);
        if (response.success) {
          await loadCompanyImports(companyFilter);
          setCompanyDetail((d) => (d && d.id === companyId ? null : d));
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to update company review');
      } finally {
        setActionLoading(null);
      }
    },
    [companyFilter],
  );

  const handleOpenCompanyDetail = useCallback(async (companyId: string) => {
    setCompanyLoadingId(companyId);
    setError(null);
    try {
      const response = await adminGetCompanyProfileImportById(companyId);
      if (response.success && response.data?.company) {
        setCompanyDetail(response.data.company);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load company details');
    } finally {
      setCompanyLoadingId(null);
    }
  }, []);

  const handleDeleteProject = useCallback(async (projectId: string, projectName: string) => {
    const confirmed = window.confirm(`Delete "${projectName}" permanently? This cannot be undone.`);
    if (!confirmed) return;

    setActionLoading(`project-${projectId}`);
    setError(null);
    try {
      const response = await adminDeleteReviewProject(projectId);
      if (response.success) {
        await loadReviewProjects(reviewFilter);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete project');
    } finally {
      setActionLoading(null);
    }
  }, [reviewFilter]);

  if (!isOpen) return null;

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    backdropFilter: 'blur(8px)',
    zIndex: isMobile ? 200 : 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  };

  const panelStyle: CSSProperties = {
    background: 'rgba(10, 10, 20, 0.98)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '20px',
    width: '100%',
    maxWidth: isMobile ? '100%' : '640px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle: CSSProperties = {
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'white',
    fontSize: '15px',
    outline: 'none',
  };

  const selectStyle: CSSProperties = {
    padding: '10px 14px',
    borderRadius: '8px',
    background: '#1a1a2e',
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'white',
    fontSize: '13px',
    cursor: 'pointer',
    outline: 'none',
    minWidth: '140px',
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    paddingRight: '32px',
  };

  const buttonStyle = (variant: 'primary' | 'danger' | 'secondary'): CSSProperties => ({
    padding: '8px 14px',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '12px',
    cursor: 'pointer',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ...(variant === 'primary' && {
      background: 'linear-gradient(to right, #14F195, #0da672)',
      color: 'black',
    }),
    ...(variant === 'danger' && {
      background: 'rgba(239, 68, 68, 0.15)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      color: '#ef4444',
    }),
    ...(variant === 'secondary' && {
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.1)',
      color: 'white',
    }),
  });

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'white' }}>Admin Panel</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '4px' }}>
              Manage Team Leads & Roles
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {/* Search */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Search Users
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by username or name..."
              style={inputStyle}
            />
          </div>

          {/* Error */}
          {error && (
            <div style={{ padding: '12px 16px', borderRadius: '10px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {/* Search Results */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ width: '24px', height: '24px', border: '2px solid rgba(20,241,149,0.3)', borderTopColor: '#14F195', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
            </div>
          )}

          {!loading && searchResults.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Search Results ({searchResults.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <img
                      src={user.xAvatarUrl || '/default-avatar.png'}
                      alt={user.xDisplayName}
                      style={{ width: '40px', height: '40px', borderRadius: '10px', objectFit: 'cover' }}
                    />
                    <div style={{ flex: 1, minWidth: '120px' }}>
                      <div style={{ fontWeight: 500, color: 'white', fontSize: '14px' }}>
                        {user.xDisplayName}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                        @{user.xUsername}
                      </div>
                      {user.role !== 'member' && (
                        <span style={{
                          display: 'inline-block',
                          marginTop: '4px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          fontWeight: 600,
                          borderRadius: '4px',
                          background: user.role === 'lead' ? 'rgba(20, 241, 149, 0.15)' : 'rgba(153, 69, 255, 0.15)',
                          color: user.role === 'lead' ? '#14F195' : '#9945FF',
                          textTransform: 'uppercase',
                        }}>
                          {user.role}
                        </span>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {user.role !== 'lead' && (
                        <>
                          <select
                            value={selectedSuperteam[user.id] || ''}
                            onChange={(e) => setSelectedSuperteam(prev => ({ ...prev, [user.id]: e.target.value }))}
                            style={selectStyle}
                          >
                            <option value="">Select Team</option>
                            {superteams.map((st) => (
                              <option key={st.id} value={st.id}>{st.name.replace('Superteam ', '')}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleSetRole(user.id, 'lead')}
                            disabled={actionLoading === user.id || !selectedSuperteam[user.id]}
                            style={{
                              ...buttonStyle('primary'),
                              opacity: actionLoading === user.id || !selectedSuperteam[user.id] ? 0.5 : 1,
                            }}
                          >
                            {actionLoading === user.id ? '...' : 'Make Lead'}
                          </button>
                        </>
                      )}
                      {user.role === 'lead' && (
                        <button
                          onClick={() => handleSetRole(user.id, 'member')}
                          disabled={actionLoading === user.id}
                          style={{
                            ...buttonStyle('danger'),
                            opacity: actionLoading === user.id ? 0.5 : 1,
                          }}
                        >
                          {actionLoading === user.id ? '...' : 'Remove Lead'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Current Leads */}
          <div>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Current Team Leads ({leads.length})
            </h3>
            {leads.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                No team leads assigned yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {leads.map((lead) => (
                  <div
                    key={lead.id}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '12px',
                      background: 'rgba(20, 241, 149, 0.05)',
                      border: '1px solid rgba(20, 241, 149, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <img
                      src={lead.xAvatarUrl || '/default-avatar.png'}
                      alt={lead.xDisplayName}
                      style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'cover' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, color: 'white', fontSize: '14px' }}>
                        @{lead.xUsername}
                      </div>
                      <div style={{ color: '#14F195', fontSize: '12px' }}>
                        {lead.superteamName || 'No team assigned'}
                      </div>
                    </div>
                    <button
                      onClick={() => handleSetRole(lead.id, 'member')}
                      disabled={actionLoading === lead.id}
                      style={{
                        ...buttonStyle('danger'),
                        opacity: actionLoading === lead.id ? 0.5 : 1,
                      }}
                    >
                      {actionLoading === lead.id ? '...' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Project Review Queue */}
          <div style={{ marginTop: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '10px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Project Review Queue ({reviewProjects.length})
              </h3>
              <select
                value={reviewFilter}
                onChange={(e) => {
                  const next = e.target.value as 'pending_review' | 'needs_more_data' | 'approved' | 'all';
                  setReviewFilter(next);
                  loadReviewProjects(next);
                }}
                style={{ ...selectStyle, minWidth: '170px' }}
              >
                <option value="pending_review">Pending review</option>
                <option value="needs_more_data">Needs more data</option>
                <option value="approved">Approved</option>
                <option value="all">All drafts</option>
              </select>
            </div>

            {reviewProjects.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.4)', fontSize: '13px', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: '10px' }}>
                No projects in this review bucket.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {reviewProjects.map((project) => (
                  <div key={project.id} style={{ padding: '12px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: 'white', fontSize: '14px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {project.name}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', marginTop: '2px' }}>
                          Team: {project.teamName} {project.ownerXUsername ? `· @${project.ownerXUsername}` : ''}
                        </div>
                        {project.website && (
                          <a href={project.website} target="_blank" rel="noopener noreferrer" style={{ color: '#14F195', fontSize: '12px', textDecoration: 'none' }}>
                            {project.website}
                          </a>
                        )}
                      </div>
                      <span style={{ fontSize: '10px', fontWeight: 600, padding: '3px 6px', borderRadius: '6px', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {project.reviewStatus}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      <button
                        onClick={() => handleOpenReviewModal(project.id)}
                        disabled={reviewLoadingProjectId === project.id}
                        style={{ ...buttonStyle('secondary'), opacity: reviewLoadingProjectId === project.id ? 0.6 : 1 }}
                      >
                        {reviewLoadingProjectId === project.id ? 'Loading...' : 'Review'}
                      </button>
                      <button
                        onClick={() => handleReviewAction(project.id, 'approve')}
                        disabled={actionLoading === `project-${project.id}`}
                        style={{ ...buttonStyle('primary'), opacity: actionLoading === `project-${project.id}` ? 0.6 : 1 }}
                      >
                        Approve + Publish
                      </button>
                      <button
                        onClick={() => handleReviewAction(project.id, 'needs_more_data')}
                        disabled={actionLoading === `project-${project.id}`}
                        style={{ ...buttonStyle('secondary'), opacity: actionLoading === `project-${project.id}` ? 0.6 : 1 }}
                      >
                        Needs More Data
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project.id, project.name)}
                        disabled={actionLoading === `project-${project.id}`}
                        style={{ ...buttonStyle('danger'), opacity: actionLoading === `project-${project.id}` ? 0.6 : 1 }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Imported company / project queue (X + website scrape) */}
          <div style={{ marginTop: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '10px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Company imports ({companyImports.length})
              </h3>
              <select
                value={companyFilter}
                onChange={(e) => {
                  const next = e.target.value as CompanyProfileReviewStatus | 'all';
                  setCompanyFilter(next);
                  loadCompanyImports(next);
                }}
                style={{ ...selectStyle, minWidth: '160px' }}
              >
                <option value="pending_review">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="all">All</option>
              </select>
            </div>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '12px', lineHeight: 1.45 }}>
              Run <code style={{ color: '#14F195' }}>npm run db:import-company-profiles</code> in backend to load{' '}
              <code style={{ color: 'rgba(255,255,255,0.7)' }}>reports/company-profiles-enriched-latest.json</code>.
            </p>
            {companyImports.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.4)', fontSize: '13px', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: '10px' }}>
                No company imports in this bucket.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {companyImports.map((c) => (
                  <div key={c.id} style={{ padding: '12px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                      <div style={{ display: 'flex', gap: '10px', minWidth: 0, flex: 1 }}>
                        {(c.profileImageUrl || c.webHeroImage) && (
                          <img
                            src={c.webHeroImage || c.profileImageUrl || ''}
                            alt=""
                            style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }}
                          />
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>
                            {c.webTitle || c.name || `@${c.xUsername}`}
                          </div>
                          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', marginTop: '2px' }}>
                            @{c.xUsername}
                            {c.referencedByCount > 0 ? ` · ${c.referencedByCount} builder refs` : ''}
                          </div>
                          {(c.websiteUrl || c.webDescription) && (
                            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '12px', marginTop: '6px', lineHeight: 1.4 }}>
                              {c.webDescription ? `${c.webDescription.slice(0, 140)}${(c.webDescription.length > 140 ? '…' : '')}` : ''}
                              {c.websiteUrl && (
                                <div style={{ marginTop: '4px' }}>
                                  <a href={c.websiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#14F195', textDecoration: 'none' }}>
                                    {c.websiteUrl}
                                  </a>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <span style={{ fontSize: '10px', fontWeight: 600, padding: '3px 6px', borderRadius: '6px', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {c.reviewStatus}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => handleOpenCompanyDetail(c.id)}
                        disabled={companyLoadingId === c.id}
                        style={{ ...buttonStyle('secondary'), opacity: companyLoadingId === c.id ? 0.6 : 1 }}
                      >
                        {companyLoadingId === c.id ? 'Loading…' : 'Details'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCompanyReviewAction(c.id, 'approve')}
                        disabled={actionLoading === `company-${c.id}` || c.reviewStatus === 'approved'}
                        style={{ ...buttonStyle('primary'), opacity: actionLoading === `company-${c.id}` ? 0.6 : 1 }}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCompanyReviewAction(c.id, 'reject')}
                        disabled={actionLoading === `company-${c.id}` || c.reviewStatus === 'rejected'}
                        style={{ ...buttonStyle('danger'), opacity: actionLoading === `company-${c.id}` ? 0.6 : 1 }}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {reviewProjectDetail && (
        <ProjectViewer
          project={reviewProjectDetail}
          isOpen={true}
          onClose={() => setReviewProjectDetail(null)}
          canEdit={false}
        />
      )}
      {companyDetail && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 220,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => setCompanyDetail(null)}
        >
          <div
            style={{
              background: '#12121c',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '16px',
              maxWidth: '560px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'auto',
              padding: '20px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, color: 'white', fontSize: '17px' }}>@{companyDetail.xUsername}</h3>
                <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>
                  {companyDetail.reviewStatus}
                  {companyDetail.sourceLabel ? ` · ${companyDetail.sourceLabel}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCompanyDetail(null)}
                style={{ ...buttonStyle('secondary'), flexShrink: 0 }}
              >
                Close
              </button>
            </div>
            <pre
              style={{
                margin: 0,
                padding: '12px',
                borderRadius: '10px',
                background: 'rgba(0,0,0,0.35)',
                color: 'rgba(255,255,255,0.85)',
                fontSize: '11px',
                lineHeight: 1.45,
                overflow: 'auto',
                maxHeight: '55vh',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(companyDetail.payload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
